/*
 * Lemonade Training Oy copy rights all right reserved.
 * Box Grant Contracts (F023 M2)
 *
 * The staff-grant path: RoleAudit rows, AccessNotices, the grant/remove/invite
 * callables, and the limits those callables enforce. Every const here must
 * survive to dist/ as real JavaScript — functions/ is plain JS and validates
 * request values against these lists at runtime.
 */

import type { Timestamp } from "firebase/firestore";
import type { BoxMemberPermissions, BoxMemberStatus, BoxRole } from "./box";
import type { InviteBoxRole } from "./invite";

/* -------------- [ RoleAudit — Boxes/{boxID}/RoleAudit/{auto} ] ------------------ */
export const ROLE_AUDIT_ACTIONS = [
  "grant_admin",
  "revoke_admin",
  "set_can_create_programs",
  "remove_staff",
  "invite_sent",
  "invite_revoked",
  "invite_accepted",
  "invite_expired_sender_not_owner",
  "invite_expired_already_staff",
  "handoff_reassign",
  "handoff_keep",
  "handoff_end",
  "owner_backfill",
  "owner_transfer",
] as const;
export type RoleAuditAction = (typeof ROLE_AUDIT_ACTIONS)[number];

export type RoleGrantSnapshot = {
  roles: BoxRole[];
  permissions: BoxMemberPermissions;
};

/** Hand-off actions whose row tracks the post-commit WorkoutCards.CoachUID rewrite. */
export const HANDOFF_AUDIT_ACTIONS = [
  "handoff_reassign",
  "handoff_keep",
] as const;
export type HandoffAuditAction = (typeof HANDOFF_AUDIT_ACTIONS)[number];

/**
 * Written as an explicit null in the transaction: the reconciler finds
 * unfinished rewrites with `where cardsRewritten == null`, which only matches
 * documents that carry the field.
 */
type CardsRewritten = number | null;

type RoleAuditBase = {
  id?: string;
  boxID: string;
  targetUID: string;
  /** "system" for scripts. */
  actorUID: string;
  /** Absent on invite_* rows (no grant existed yet). */
  before?: RoleGrantSnapshot;
  after?: RoleGrantSnapshot;
  grantVersion: number;
  /** invite_* rows only. */
  invitationID?: string;
  /** The audit id this change reverses. */
  undoOf?: string;
  createdAt: Timestamp | Date;
};

/** uids only — never a display name or email. */
export type RoleAuditEntry =
  | (RoleAuditBase & {
      action: Exclude<RoleAuditAction, HandoffAuditAction | "handoff_end">;
      programmeUID?: never;
      targetCoachUID?: never;
      cardsRewritten?: never;
    })
  | (RoleAuditBase & {
      action: "handoff_end";
      programmeUID: string;
      targetCoachUID?: never;
      cardsRewritten?: never;
    })
  | (RoleAuditBase & {
      action: "handoff_reassign";
      programmeUID: string;
      targetCoachUID: string;
      cardsRewritten: CardsRewritten;
    })
  | (RoleAuditBase & {
      action: "handoff_keep";
      programmeUID: string;
      targetCoachUID?: never;
      cardsRewritten: CardsRewritten;
    });

/* -------------- [ AccessNotices — Users/{uid}/AccessNotices/{auto} ] ------------------ */
export const HANDOFF_OUTCOMES = ["reassigned", "kept_by_gym", "ended"] as const;
export type HandoffOutcome = (typeof HANDOFF_OUTCOMES)[number];

export const ACCESS_NOTICE_KINDS = [
  "promoted_admin",
  "demoted_admin",
  "creation_grant_changed",
  "removed_from_staff",
  "programme_handoff",
  "programme_received",
] as const;
export type AccessNoticeKind = (typeof ACCESS_NOTICE_KINDS)[number];

/** The writer sets only the name field the recipient is entitled to; no notice carries both. */
export type AccessNoticeProgramme = {
  programmeUID: string;
  name: string;
  outcome: HandoffOutcome;
  /** Athlete-facing: the same-gym coach they now work with. */
  targetCoachDisplayName?: string;
  /** Receiving-coach-facing only. */
  athleteFirstName?: string;
};

/** Server-written and self-contained: the recipient may no longer be able to read the box. */
export type AccessNotice = {
  id?: string;
  kind: AccessNoticeKind;
  boxID: string;
  gymName: string;
  /** Attribution by role only; the actor uid lives in RoleAudit. */
  changedByRole: "owner" | "admin";
  grantVersion: number;
  programmes?: AccessNoticeProgramme[];
  createdAt: Timestamp | Date;
  /** Required, written as an explicit null: `where readAt == null` only matches documents that carry the field. */
  readAt: Timestamp | Date | null;
};

/* -------------- [ Limits ] ------------------ */
/** Callables reject above it, never clamp. */
export const REMOVE_BOX_STAFF_MAX_HANDOFFS = 50;
/** Per (actor, target) per hour. Whether the 11th call is refused or only its notice suppressed is the grant routine's rule. */
export const SET_BOX_MEMBER_ROLES_RATE_PER_HOUR = 10;
/** Per box per hour; callables reject above it. */
export const INVITE_BOX_STAFF_RATE_PER_HOUR = 20;
export const RESEND_BOX_INVITE_MIN_INTERVAL_MS = 5 * 60 * 1000;
/** Client-authored free text rendered in the invite email; the template treats it as text, never HTML. */
export const INVITE_MESSAGE_MAX_LENGTH = 500;

/* -------------- [ setBoxMemberRoles ] ------------------ */
export type SetBoxMemberRolesRequest = {
  boxID: string;
  targetUID: string;
  /** Non-empty, no duplicates. Admin-bit changes are owner-only. */
  roles: BoxRole[];
  /** Only canCreatePrograms is honoured; the routine derives the rest. Ignored for admins. */
  permissions?: Pick<BoxMemberPermissions, "canCreatePrograms">;
  /** From the client's Box listener; a mismatch is failed-precondition. */
  expectedGrantVersion: number;
  /** Ledger link only: the server verifies it names a RoleAudit row of this box, and an undo passes the same permission matrix as any grant. */
  undoOf?: string;
};

export type SetBoxMemberRolesResponse = {
  grantVersion: number;
  auditID: string;
  roles: BoxRole[];
  role: BoxRole;
  permissions: BoxMemberPermissions;
  /** The wider claims could not be minted after commit; the reconciler finishes within 15 minutes. */
  claimsPending: boolean;
};

/* -------------- [ removeBoxStaff ] ------------------ */
export type ProgrammeHandoff = {
  programmeUID: string;
  outcome: HandoffOutcome;
  /** Required when outcome is "reassigned". */
  targetCoachUID?: string;
};

export type RemoveBoxStaffRequest = {
  boxID: string;
  targetUID: string;
  /** Exactly one entry per PT programme the target owns at this box; at most REMOVE_BOX_STAFF_MAX_HANDOFFS. */
  handoffs: ProgrammeHandoff[];
  expectedGrantVersion: number;
};

export type RemoveBoxStaffResponse = {
  grantVersion: number;
  auditID: string;
  handedOff: { reassigned: number; keptByGym: number; ended: number };
  /** No membership remained, so the person was signed out of the gym. */
  signedOut: boolean;
  /** The member relationship survived (roles are now ["athlete"]). */
  membershipKept: boolean;
  claimsPending: boolean;
};

/* -------------- [ inviteBoxStaff / revokeBoxInvite / resendBoxInvite ] ------------------ */
export type InviteBoxStaffRequest = {
  boxID: string;
  recipientEmail: string;
  /** "admin" requires the caller to be Box.ownerUID; "coach" requires owner or admin. */
  boxRole: InviteBoxRole;
  /** Coach invites only; admins always get it. */
  canCreatePrograms?: boolean;
  /** At most INVITE_MESSAGE_MAX_LENGTH characters. */
  message?: string;
};

/** Neutral on purpose: "no such user" and "already invited" are indistinguishable to the caller. */
export type InviteBoxStaffResponse = {
  invitationID: string | null;
  queued: true;
};

export type RevokeBoxInviteRequest = { boxID: string; invitationID: string };
export type ResendBoxInviteRequest = { boxID: string; invitationID: string };

/* -------------- [ previewBoxOwnerBackfill (platform admin) ] ------------------ */
// Response-only: the server writes it and nothing validates it at runtime, so no list is exported.
export type BoxOwnerBackfillEligibility =
  "eligible" | "not_eligible_platform_account" | "manual_tie" | "no_admins";

export type BoxOwnerBackfillCandidate = {
  boxID: string;
  boxName: string;
  currentOwnerUID: string | null;
  candidateUID: string | null;
  /** Resolved from Auth server-side for the admin table; never denormalised into Firestore. */
  candidateDisplayName: string | null;
  candidateEmail: string | null;
  eligibility: BoxOwnerBackfillEligibility;
  /** ISO 8601 — the candidate's Members.joinedAt. Callable JSON cannot carry a Timestamp. */
  joinedAt: string | null;
};

/* -------------- [ getBoxStaffDisplayNames ] ------------------ */
export type BoxStaffDisplayName = {
  uid: string;
  name: string;
  role: BoxRole;
  status: BoxMemberStatus;
  /** ISO 8601. */
  joinedAt: string | null;
  canCreatePrograms: boolean;
};

export type GetBoxStaffDisplayNamesResponse = {
  /** Active members only; pending invites live in pendingInviteSenders. */
  staff: BoxStaffDisplayName[];
  ownerUID: string | null;
  /** uid → display name for the senders of pending staff invites. */
  pendingInviteSenders: Record<string, string>;
};
